class CartDrawer extends HTMLElement {
  constructor() {
    super()

    this.addEventListener('keyup', (evt) => {
      evt.code === 'Escape' && this.close()
    })
    
    // Add overlay click handler with null check
    const overlay = this.querySelector('#CartDrawer-Overlay')
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this))
    }
    
    // Bind close handler for reuse
    this.closeHandler = this.close.bind(this)
    
  this.setHeaderCartIconAccessibility()
  this.setSavingsMessage()
  this.lastProgressBarTrigger = 0
  this.progressBarAnimationTimeout = null
  this.progressBarCompletionTimeout = null
  this.isProgressBarAnimating = false

  document.addEventListener('update:cart-line-item', () => {
    this.setSavingsMessage()
    // Trigger progress bar animation when cart updates (only if drawer is already open)
    // This handles quantity changes and removals, but avoids double-trigger when
    // renderContents() calls open() for upsell adds (which already triggers progress bar)
    const now = Date.now()
    
    // If drawer is active and enough time has passed since last trigger
    if (this.classList.contains('active') && (now - this.lastProgressBarTrigger) > 500) {
      // If animation is already in progress, restart it to reflect the new update
      if (this.isProgressBarAnimating) {
        this.restartProgressBarAnimation()
      } else {
        // Start new animation
        // Clear any pending animation timeout
        if (this.progressBarAnimationTimeout) {
          clearTimeout(this.progressBarAnimationTimeout)
        }
        
        this.progressBarAnimationTimeout = setTimeout(() => {
          this.animateProgressBar()
          this.lastProgressBarTrigger = Date.now()
          this.progressBarAnimationTimeout = null
        }, 100)
      }
    }
  })
  
  // Initialize upsell add to cart buttons
  this.initializeUpsellButtons()
}

initializeUpsellButtons() {
  // Use event delegation for upsell buttons
  this.addEventListener('click', (e) => {
    const upsellBtn = e.target.closest('.upsell-add-btn')
    if (upsellBtn && !upsellBtn.disabled && !upsellBtn.classList.contains('loading')) {
      e.preventDefault()
      this.addUpsellToCart(upsellBtn)
    }
  })
}

async addUpsellToCart(button) {
  const variantId = button.dataset.variantId
  const quantity = parseInt(button.dataset.quantity) || 1
  const upsellItem = button.closest('.upsell-item')
  
  if (!variantId) return
  
  // Add loading state
  button.classList.add('loading')
  button.disabled = true
  const btnText = button.querySelector('.btn-text')
  if (!btnText) {
    console.error('Button text element not found')
    button.disabled = false
    return
  }
  const originalText = btnText.textContent
  btnText.textContent = 'ADDING...'
  
  try {
    // Ensure variantId is a string (Shopify API requirement)
    const variantIdStr = String(variantId)
    
    // Add item to cart via AJAX with line item properties to track upsell
    const cartItem = {
      id: variantIdStr,
      quantity: quantity,
      properties: {
        '_added_from_location': 'cart_drawer'
      }
    }
    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [cartItem]
      })
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.description || 'Failed to add item to cart')
    }
    
    // Get the line item key from the response
    const addedItem = await response.json()
    const addedItemKey = addedItem.key || `${variantIdStr}:${quantity}`
    
    // Fetch updated cart sections (required for renderContents)
    const sectionsToFetch = this.getSectionsToRender().map(section => section.id).join(',')
    const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) ? routes.cart_url : '/cart'
    const sectionsResponse = await fetch(`${cartUrl}?sections=${sectionsToFetch}`)
    
    if (!sectionsResponse.ok) {
      throw new Error('Failed to fetch updated cart sections')
    }
    
    const sectionsData = await sectionsResponse.json()
    
    if (!sectionsData) {
      throw new Error('Invalid sections data received')
    }
    
    // Update cart drawer content with sections
    await this.renderContents({ sections: sectionsData })
    
    // Also fetch cart for item count
    const cartResponse = await fetch('/cart.js')
    if (cartResponse.ok) {
      const cart = await cartResponse.json()
      if (cart && cart.item_count !== undefined) {
        this.updateCartCount(cart.item_count)
      }
    }
    
    // Find and highlight the specific newly added item
    setTimeout(() => {
      const cartItemsContainer = this.querySelector('cart-drawer-items')
      if (cartItemsContainer) {
        // Find the cart item with matching variant ID
        const addedCartItem = this.querySelector(`.cart-item[data-variant-id="${variantIdStr}"]`)
        
        if (addedCartItem) {
          // Scroll to show the newly added item
          addedCartItem.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest'
          })
          
          // Add highlight effect - simple opacity flash
          addedCartItem.style.transition = 'opacity 0.15s ease'
          addedCartItem.style.opacity = '0.5'
          
          // Flash back to full opacity
          setTimeout(() => {
            addedCartItem.style.opacity = '1'
          }, 150)
          
          // Second flash after a moment
          setTimeout(() => {
            addedCartItem.style.opacity = '0.7'
          }, 400)
          
          setTimeout(() => {
            addedCartItem.style.opacity = '1'
          }, 550)
        }
      }
    }, 150)
    
    // Hide the upsell item after a short delay
    setTimeout(() => {
      if (upsellItem) {
        upsellItem.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out'
        upsellItem.style.opacity = '0'
        upsellItem.style.transform = 'scale(0.95)'
        
        setTimeout(() => {
          upsellItem.remove()
          
          // Check if all upsells are gone, hide the section
          const upsellsGrid = this.querySelector('.upsells-grid')
          if (upsellsGrid && upsellsGrid.children.length === 0) {
            const upsellsSection = this.querySelector('.cart-upsells')
            if (upsellsSection) {
              upsellsSection.style.display = 'none'
            }
          }
        }, 300)
      }
    }, 1000)
    
  } catch (error) {
    // Log error for debugging (remove console.error in production if preferred)
    console.error('Error adding upsell to cart:', error)
    
    // Reset button state
    if (btnText) {
      btnText.textContent = originalText
    }
    button.classList.remove('loading')
    button.disabled = false
    
    // Show user-friendly error message
    const errorMessage = error.message || 'Failed to add item to cart. Please try again.'
    
    // Try to show error in cart drawer if possible
    let errorElement = this.querySelector('.cart-drawer__error-message')
    
    // Create error element if it doesn't exist
    if (!errorElement) {
      const drawerInner = this.querySelector('.drawer__inner')
      if (drawerInner) {
        errorElement = document.createElement('div')
        errorElement.className = 'cart-drawer__error-message'
        errorElement.style.cssText = 'padding: 1rem; margin: 1rem; background-color: #fee; color: #c33; border: 1px solid #fcc; border-radius: 4px;'
        drawerInner.insertBefore(errorElement, drawerInner.firstChild)
      }
    }
    
    if (errorElement) {
      errorElement.textContent = errorMessage
      errorElement.style.display = 'block'
      setTimeout(() => {
        errorElement.style.display = 'none'
      }, 5000)
    }
  }
}

updateCartCount(count) {
  const cartCount = document.querySelector('#cart-icon-bubble span')
  if (cartCount) {
    cartCount.textContent = count
  }
}

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble')
    if (!cartLink) return
    
    cartLink.setAttribute('role', 'button')
    cartLink.setAttribute('aria-haspopup', 'dialog')
    cartLink.addEventListener('click', (event) => {
      event.preventDefault()
      this.open(cartLink)
    })
    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault()
        this.open(cartLink)
      }
    })
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy)
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary')
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote)
    
    // Prevent duplicate open calls
    if (this.classList.contains('active')) {
      return
    }
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('active')
    })

    // Trigger progress bar animation (only if not already animating)
    // Set timestamp FIRST to prevent event listener from triggering duplicate animation
    if (!this.isProgressBarAnimating) {
      this.lastProgressBarTrigger = Date.now()
      this.animateProgressBar()
    }

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer')
        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close')
        trapFocus(containerToTrapFocusOn, focusElement)
      },
      { once: true }
    )

    document.body.classList.add('overflow-hidden')
  }

  animateProgressBar() {
    // Prevent multiple simultaneous animations
    if (this.isProgressBarAnimating) {
      return
    }
    
    const progressBarContainer = this.querySelector('.progress-bar-container')
    const progressBar = this.querySelector('.progress-bar-fill')
    const whiteTextLayer = this.querySelector('.trust-badges-white')
    const whiteBorderLayer = this.querySelector('.trust-border-white')
    
    if (!progressBar) {
      return
    }
    
    if (!progressBarContainer) {
      console.warn('Progress bar container not found')
      return
    }
    
    // Calculate target width based on thresholds (dynamic 3-badge system)
    let targetWidth = '0%' // Default to empty, not full
    const totalBadges = 3 // Always 3 visible badges
    const freeShippingNotAchieved = progressBarContainer.dataset.freeShippingNotAchieved === 'true'
    const premiumNotAchieved = progressBarContainer.dataset.premiumNotAchieved === 'true'
    const eliteNotAchieved = progressBarContainer.dataset.eliteNotAchieved === 'true'
    const enableElitePhase = progressBarContainer.dataset.enableElitePhase === 'true'
    
    // Determine how many badges to fill based on what's achieved
    let badgesToFill = totalBadges // Default: fill all badges
    
    if (enableElitePhase) {
      // 4-phase mode with dynamic shifting
      if (freeShippingNotAchieved) {
        badgesToFill = 1
      } else if (premiumNotAchieved) {
        badgesToFill = 2
      } else if (eliteNotAchieved) {
        badgesToFill = 2
      }
    } else {
      // 3-phase mode (no shifting)
      if (freeShippingNotAchieved) {
        badgesToFill = 1
      } else if (premiumNotAchieved) {
        badgesToFill = 2
      }
    }
    
    if (badgesToFill < totalBadges) {
      // Calculate percentage and subtract buffer to stop before border
      const basePercentage = (badgesToFill / totalBadges) * 100
      let buffer = 1 // Default buffer
      
      // Use smaller buffer for first badge only to fill it more completely
      if (badgesToFill === 1) {
        buffer = window.innerWidth < 768 ? -1.5 : -1.2
      } else {
        buffer = window.innerWidth < 768 ? 1.5 : 1
      }
      
      const targetPercentage = basePercentage - buffer
      targetWidth = `${targetPercentage}%`
    } else {
      targetWidth = '100%'
    }
    
    // Mark animation as in progress
    this.isProgressBarAnimating = true
    
    // Clear any existing animation timeouts
    if (this.progressBarAnimationTimeout) {
      clearTimeout(this.progressBarAnimationTimeout)
      this.progressBarAnimationTimeout = null
    }
    
    // Clear any existing completion timeout
    if (this.progressBarCompletionTimeout) {
      clearTimeout(this.progressBarCompletionTimeout)
      this.progressBarCompletionTimeout = null
    }
    
    // Reset animation
    progressBar.style.width = '0%'
    progressBar.style.transition = 'none'
    
    if (whiteTextLayer) {
      whiteTextLayer.style.clipPath = 'inset(0 100% 0 0)'
      whiteTextLayer.style.transition = 'none'
    }
    
    if (whiteBorderLayer) {
      whiteBorderLayer.style.clipPath = 'inset(0 100% 0 0)'
      whiteBorderLayer.style.transition = 'none'
    }
    
    // Start animation after a short delay
    this.progressBarAnimationTimeout = setTimeout(() => {
      progressBar.style.transition = 'width 3s cubic-bezier(0.4, 0, 0.2, 1)'
      progressBar.style.width = targetWidth
      
      if (whiteTextLayer) {
        whiteTextLayer.style.transition = 'clip-path 3s cubic-bezier(0.4, 0, 0.2, 1)'
        // Calculate clip-path based on target width
        if (targetWidth === '100%') {
          whiteTextLayer.style.clipPath = 'inset(0 0 0 0)'
        } else {
          const clipValue = 100 - parseFloat(targetWidth)
          whiteTextLayer.style.clipPath = `inset(0 ${clipValue}% 0 0)`
        }
      }
      
      if (whiteBorderLayer) {
        whiteBorderLayer.style.transition = 'clip-path 3s cubic-bezier(0.4, 0, 0.2, 1)'
        // Calculate clip-path based on target width
        if (targetWidth === '100%') {
          whiteBorderLayer.style.clipPath = 'inset(0 0 0 0)'
        } else {
          const clipValue = 100 - parseFloat(targetWidth)
          whiteBorderLayer.style.clipPath = `inset(0 ${clipValue}% 0 0)`
        }
      }
      
      // Reset animation flag after animation completes (3s + buffer)
      this.progressBarCompletionTimeout = setTimeout(() => {
        this.isProgressBarAnimating = false
        this.progressBarAnimationTimeout = null
        this.progressBarCompletionTimeout = null
      }, 3200)
    }, 100)
  }

  restartProgressBarAnimation() {
    // Restart the animation by resetting it and clearing the completion timeout
    const progressBarContainer = this.querySelector('.progress-bar-container')
    const progressBar = this.querySelector('.progress-bar-fill')
    const whiteTextLayer = this.querySelector('.trust-badges-white')
    const whiteBorderLayer = this.querySelector('.trust-border-white')
    
    if (!progressBar) {
      return
    }
    
    if (!progressBarContainer) {
      console.warn('Progress bar container not found in restart')
      return
    }
    
    // Calculate target width based on thresholds (dynamic 3-badge system)
    let targetWidth = '0%' // Default to empty, not full
    const totalBadges = 3 // Always 3 visible badges
    const freeShippingNotAchieved = progressBarContainer.dataset.freeShippingNotAchieved === 'true'
    const premiumNotAchieved = progressBarContainer.dataset.premiumNotAchieved === 'true'
    const eliteNotAchieved = progressBarContainer.dataset.eliteNotAchieved === 'true'
    const enableElitePhase = progressBarContainer.dataset.enableElitePhase === 'true'
    
    // Determine how many badges to fill based on what's achieved
    let badgesToFill = totalBadges // Default: fill all badges
    
    if (enableElitePhase) {
      // 4-phase mode with dynamic shifting
      if (freeShippingNotAchieved) {
        badgesToFill = 1
      } else if (premiumNotAchieved) {
        badgesToFill = 2
      } else if (eliteNotAchieved) {
        badgesToFill = 2
      }
    } else {
      // 3-phase mode (no shifting)
      if (freeShippingNotAchieved) {
        badgesToFill = 1
      } else if (premiumNotAchieved) {
        badgesToFill = 2
      }
    }
    
    if (badgesToFill < totalBadges) {
      // Calculate percentage and subtract buffer to stop before border
      const basePercentage = (badgesToFill / totalBadges) * 100
      let buffer = 1 // Default buffer
      
      // Use smaller buffer for first badge only to fill it more completely
      if (badgesToFill === 1) {
        buffer = window.innerWidth < 768 ? -1.5 : -1.2
      } else {
        buffer = window.innerWidth < 768 ? 1.5 : 1
      }
      
      const targetPercentage = basePercentage - buffer
      targetWidth = `${targetPercentage}%`
    } else {
      targetWidth = '100%'
    }
    
    // Clear the completion timeout so animation doesn't end prematurely
    if (this.progressBarCompletionTimeout) {
      clearTimeout(this.progressBarCompletionTimeout)
      this.progressBarCompletionTimeout = null
    }
    
    // Reset animation back to start
    progressBar.style.width = '0%'
    progressBar.style.transition = 'none'
    
    if (whiteTextLayer) {
      whiteTextLayer.style.clipPath = 'inset(0 100% 0 0)'
      whiteTextLayer.style.transition = 'none'
    }
    
    if (whiteBorderLayer) {
      whiteBorderLayer.style.clipPath = 'inset(0 100% 0 0)'
      whiteBorderLayer.style.transition = 'none'
    }
    
    // Restart animation after a brief moment
    setTimeout(() => {
      progressBar.style.transition = 'width 3s cubic-bezier(0.4, 0, 0.2, 1)'
      progressBar.style.width = targetWidth
      
      if (whiteTextLayer) {
        whiteTextLayer.style.transition = 'clip-path 3s cubic-bezier(0.4, 0, 0.2, 1)'
        // Calculate clip-path based on target width
        if (targetWidth === '100%') {
          whiteTextLayer.style.clipPath = 'inset(0 0 0 0)'
        } else {
          const clipValue = 100 - parseFloat(targetWidth)
          whiteTextLayer.style.clipPath = `inset(0 ${clipValue}% 0 0)`
        }
      }
      
      if (whiteBorderLayer) {
        whiteBorderLayer.style.transition = 'clip-path 3s cubic-bezier(0.4, 0, 0.2, 1)'
        // Calculate clip-path based on target width
        if (targetWidth === '100%') {
          whiteBorderLayer.style.clipPath = 'inset(0 0 0 0)'
        } else {
          const clipValue = 100 - parseFloat(targetWidth)
          whiteBorderLayer.style.clipPath = `inset(0 ${clipValue}% 0 0)`
        }
      }
      
      // Set new completion timeout
      this.progressBarCompletionTimeout = setTimeout(() => {
        this.isProgressBarAnimating = false
        this.progressBarCompletionTimeout = null
      }, 3200)
    }, 50)
    
    // Update the last trigger timestamp
    this.lastProgressBarTrigger = Date.now()
  }

  close() {
    // Prevent duplicate close calls
    if (!this.classList.contains('active')) {
      return
    }
    
    this.classList.remove('active')
    document.body.classList.remove('overflow-hidden')
    // removeTrapFocus(this.activeElement)
    
    // Clear any pending animation timeouts
    if (this.progressBarAnimationTimeout) {
      clearTimeout(this.progressBarAnimationTimeout)
      this.progressBarAnimationTimeout = null
    }
    
    // Clear completion timeout
    if (this.progressBarCompletionTimeout) {
      clearTimeout(this.progressBarCompletionTimeout)
      this.progressBarCompletionTimeout = null
    }
    
    // Reset progress bar, white text layer, and white border
    const progressBar = this.querySelector('.progress-bar-fill')
    const whiteTextLayer = this.querySelector('.trust-badges-white')
    const whiteBorderLayer = this.querySelector('.trust-border-white')
    
    if (progressBar) {
      progressBar.style.transition = 'none'
      progressBar.style.width = '0%'
    }
    
    if (whiteTextLayer) {
      whiteTextLayer.style.transition = 'none'
      whiteTextLayer.style.clipPath = 'inset(0 100% 0 0)'
    }
    
    if (whiteBorderLayer) {
      whiteBorderLayer.style.transition = 'none'
      whiteBorderLayer.style.clipPath = 'inset(0 100% 0 0)'
    }
    
    // Reset progress bar trigger timestamp and animation flag
    this.lastProgressBarTrigger = 0
    this.isProgressBarAnimating = false
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button')
    cartDrawerNote.setAttribute('aria-expanded', 'false')

    const nextSibling = cartDrawerNote.nextElementSibling
    if (nextSibling && nextSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        nextSibling.id
      )
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      )
    })

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape)
  }

  async renderContents(parsedState) {
    if (!parsedState.sections) {
      parsedState.sections = await this.parsedStateFallback()
    }
    
    const drawerInner = this.querySelector('.drawer__inner')
    if (drawerInner) {
      drawerInner.classList.contains('is-empty') && drawerInner.classList.remove('is-empty')
    }
    
    this.productId = parsedState.id
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id)
      
      if (sectionElement && parsedState.sections[section.id]) {
        const innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        )
        if (innerHTML) {
          sectionElement.innerHTML = innerHTML
        }
      }
    })

    setTimeout(() => {
      // Re-attach overlay click handler (element may have been replaced)
      const overlay = this.querySelector('#CartDrawer-Overlay')
      if (overlay) {
        // Remove old listener if it exists to prevent duplicates
        overlay.removeEventListener('click', this.closeHandler)
        overlay.addEventListener('click', this.closeHandler)
      }
      
      // Re-initialize upsell buttons after content is rendered
      this.initializeUpsellButtons()
      
      // Only open drawer if it's not already open (prevents duplicate animations)
      const wasAlreadyOpen = this.classList.contains('active')
      if (!wasAlreadyOpen) {
        // open() will set lastProgressBarTrigger and start animation
        this.open()
      } else {
        // If already open, just trigger progress bar animation (only if not already animating)
        // Set timestamp FIRST to prevent event listener from triggering duplicate animation
        if (!this.isProgressBarAnimating) {
          this.lastProgressBarTrigger = Date.now()
          this.animateProgressBar()
        }
      }
      
      // Emit event after a delay to ensure animation flag and timestamp are set
      // The event listener checks if > 500ms have passed and if animation is not in progress
      // This delay ensures both conditions prevent duplicate trigger
      setTimeout(() => {
        emit('update:cart-line-item')
      }, 300)
    })
  }

  async parsedStateFallback() {
    const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) ? routes.cart_url : '/cart'
    const sections = await fetch(
      `${cartUrl}?sections=${this.getSectionsToRender().map(
        (section) => section.id
      )}`
    )
    return await sections.json()
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return ''
    const parsed = new DOMParser().parseFromString(html, 'text/html')
    const element = parsed.querySelector(selector)
    return element ? element.innerHTML : ''
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ]
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)
  }

  setActiveElement(element) {
    this.activeElement = element
  }

  setSavingsMessage() {
    const savings = this.querySelectorAll('[data-line-item-saving]')
    const target = this.querySelector('#CartSavings')
    
    if (!target) return
    
    const moneyFormat = window.cartStrings?.money_format || '{{amount}}'

    if (savings.length > 0) {
      const targetChild = target.querySelector('span')
      if (!targetChild) return
      
      let totalSavings = 0

      savings.forEach((saving) => {
        const savingAmount = parseInt(saving.dataset.lineItemSaving) || 0
        const quantity = parseInt(saving.dataset.lineItemQuantity) || 0
        totalSavings += (savingAmount * quantity) / 100
      })

      target.classList.remove('hidden')
      targetChild.innerHTML = moneyFormat.replace(
        '{{amount}}',
        totalSavings.toLocaleString()
      )
    } else {
      // Hide savings message if no savings
      target.classList.add('hidden')
    }
  }
}

customElements.define('cart-drawer', CartDrawer)

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ]
  }
}

customElements.define('cart-drawer-items', CartDrawerItems)
